import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  TextField,
  CircularProgress,
} from '@mui/material';
import {
  Business,
  Person,
  HomeWork,
} from '@mui/icons-material';
import { useAuthStore } from '../store/authStore';
import { UserRole } from '../types';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import LanguageSwitcher from '../components/LanguageSwitcher';

const OnboardingFlow: React.FC = () => {
  const navigate = useNavigate();
  const { user, setUser } = useAuthStore();
  const [step, setStep] = useState<1 | 2>(1);
  const [selectedRole, setSelectedRole] = useState<UserRole | null>(null);
  const [loading, setLoading] = useState(false);
  
  // Step 2 Form State
  const [companyName, setCompanyName] = useState('');
  const [ice, setIce] = useState('');
  const [firstName, setFirstName] = useState(user?.profile.firstName || '');
  const [lastName, setLastName] = useState(user?.profile.lastName || '');

  if (!user) {
    navigate('/login');
    return null;
  }

  const handleRoleSelect = (role: UserRole) => {
    setSelectedRole(role);
    setStep(2);
  };

  const handleFinish = async () => {
    if (!selectedRole) return;
    setLoading(true);

    try {
      const userRef = doc(db, 'users', user.id);
      
      const updatedProfile = {
        ...user.profile,
        firstName,
        lastName,
        company: companyName,
        // We could store ICE here or in metadata
      };

      await updateDoc(userRef, {
        role: selectedRole,
        profile: updatedProfile,
      });

      // Update local store
      setUser({
        ...user,
        role: selectedRole,
        profile: updatedProfile,
      });

      // Route to correct dashboard
      if (selectedRole === UserRole.PROMOTEUR) navigate('/promoteur');
      else navigate('/dashboard'); // Agent/Client dashboard
      
    } catch (error) {
      console.error('Error updating user profile:', error);
      alert('Failed to complete onboarding. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', bgcolor: '#F0EDE9', p: 3, position: 'relative' }}>
      <Box sx={{ position: 'absolute', top: 24, right: 24 }}>
        <LanguageSwitcher />
      </Box>
      <Card sx={{ maxWidth: 800, width: '100%', borderRadius: 3, boxShadow: '0 12px 40px rgba(0,0,0,0.08)' }}>
        <CardContent sx={{ p: { xs: 3, md: 6 } }}>
          
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h3" sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 800, color: 'primary.main', mb: 1 }}>
              Welcome to souk.immo
            </Typography>
            <Typography variant="h6" color="text.secondary">
              {step === 1 ? "Let's personalize your experience. Who are you?" : "Just a few more details."}
            </Typography>
          </Box>

          {step === 1 && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Card 
                  variant="outlined" 
                  sx={{ 
                    height: '100%', 
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    borderColor: selectedRole === UserRole.PROMOTEUR ? 'primary.main' : 'divider',
                    borderWidth: selectedRole === UserRole.PROMOTEUR ? 2 : 1,
                    '&:hover': { borderColor: 'primary.main', transform: 'translateY(-4px)' }
                  }}
                  onClick={() => handleRoleSelect(UserRole.PROMOTEUR)}
                >
                  <CardContent sx={{ textAlign: 'center', p: 4 }}>
                    <Business sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Promoteur Immobilier</Typography>
                    <Typography variant="body2" color="text.secondary">
                      I develop and manage large-scale real estate projects (Chantiers).
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Card 
                  variant="outlined" 
                  sx={{ 
                    height: '100%', 
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    borderColor: selectedRole === UserRole.AGENT ? 'primary.main' : 'divider',
                    borderWidth: selectedRole === UserRole.AGENT ? 2 : 1,
                    '&:hover': { borderColor: 'primary.main', transform: 'translateY(-4px)' }
                  }}
                  onClick={() => handleRoleSelect(UserRole.AGENT)}
                >
                  <CardContent sx={{ textAlign: 'center', p: 4 }}>
                    <HomeWork sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Agency / Agent</Typography>
                    <Typography variant="body2" color="text.secondary">
                      I list individual properties and manage a client pipeline.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card 
                  variant="outlined" 
                  sx={{ 
                    height: '100%', 
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    borderColor: selectedRole === UserRole.CLIENT ? 'primary.main' : 'divider',
                    borderWidth: selectedRole === UserRole.CLIENT ? 2 : 1,
                    '&:hover': { borderColor: 'primary.main', transform: 'translateY(-4px)' }
                  }}
                  onClick={() => handleRoleSelect(UserRole.CLIENT)}
                >
                  <CardContent sx={{ textAlign: 'center', p: 4 }}>
                    <Person sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Buyer / Investor</Typography>
                    <Typography variant="body2" color="text.secondary">
                      I am looking to buy or rent a property in Morocco.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {step === 2 && (
            <Box sx={{ maxWidth: 500, mx: 'auto' }}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField 
                    fullWidth 
                    label="First Name" 
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField 
                    fullWidth 
                    label="Last Name" 
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                </Grid>

                {selectedRole === UserRole.PROMOTEUR && (
                  <>
                    <Grid item xs={12}>
                      <TextField 
                        fullWidth 
                        label="Company Name (Promoteur)" 
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField 
                        fullWidth 
                        label="ICE (Identifiant Commun de l'Entreprise)" 
                        value={ice}
                        onChange={(e) => setIce(e.target.value)}
                        helperText="Required for Verified Builder status"
                      />
                    </Grid>
                  </>
                )}

                {selectedRole === UserRole.AGENT && (
                  <Grid item xs={12}>
                    <TextField 
                      fullWidth 
                      label="Agency Name" 
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                    />
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
                    <Button variant="outlined" onClick={() => setStep(1)} size="large">
                      Back
                    </Button>
                    <Button 
                      variant="contained" 
                      onClick={handleFinish} 
                      disabled={loading || !firstName}
                      fullWidth 
                      size="large"
                    >
                      {loading ? <CircularProgress size={24} /> : 'Complete Setup'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}

        </CardContent>
      </Card>
    </Box>
  );
};

export default OnboardingFlow;